import { Injectable, signal } from '@angular/core';

export type ErrorMessagePart = {
  text: string;
  bold?: boolean;
};

export type ErrorModalMessage = string | ErrorMessagePart;

export interface ErrorModalState {
  show: boolean;
  title: string;
  messages: ErrorMessagePart[];
}

@Injectable({providedIn: 'root'})
export class ModalErrorService {
  private readonly _state = signal<ErrorModalState>({
    show: false,
    title: 'Error',
    messages: [],
  });

  readonly state = this._state.asReadonly();

  private pendingMessages: ErrorMessagePart[] = [];
  private flushTimer: number | null = null;

  private normalizeMessages(messages: string[] | string | ErrorModalMessage[]): ErrorMessagePart[] {
    const arr = Array.isArray(messages) ? messages : [messages];

    return arr
      .filter(Boolean)
      .map((m) => {
        if (typeof m === 'string') {
          return { text: m };
        }
        return {
          text: String(m.text || ''),
          bold: !!m.bold,
        };
      })
      .filter((m) => !!m.text.trim());
  }

  open(title: string, messages: string[] | string | ErrorModalMessage[]): void {
    this._state.set({
      show: true,
      title: title || 'Error',
      messages: this.normalizeMessages(messages),
    });
  }

  enqueue(title: string, messages: string[] | string | ErrorModalMessage[], debounceMs: number = 100): void {
    const arr = this.normalizeMessages(messages);
    this.pendingMessages.push(...arr);

    if (this.flushTimer != null) return;

    this.flushTimer = window.setTimeout(() => {
      const unique = Array.from(
        new Map(
          this.pendingMessages.map((m) => [m.text.trim(), m])
        ).values()
      );

      this.pendingMessages = [];
      this.flushTimer = null;

      if (unique.length) {
        this.open(title, unique);
      }
    }, debounceMs);
  }

  close(): void {
    this._state.update((s) => ({...s, show: false}));
  }

  extractMessages(err: any): string[] {
    const errors = err?.error?.errors;

    if (Array.isArray(errors)) {
      return errors.map((e: any) => String(e)).filter(Boolean);
    }

    if (errors && typeof errors === 'object') {
      const title = (errors as any).title;
      if (title != null && String(title).trim()) {
        return [String(title).trim()];
      }

      const out: string[] = [];
      Object.keys(errors).forEach((key) => {
        if (key === 'code') return;

        const v = (errors as any)[key];
        if (Array.isArray(v)) {
          v.forEach((m: any) => out.push(String(m)));
        } else if (v != null) {
          out.push(String(v));
        }
      });
      return out.map((m) => m.trim()).filter(Boolean);
    }

    const message = err?.error?.message || err?.message;
    return message ? [String(message)] : ['Ha ocurrido un error.'];
  }

  extractAllErrors(err: any): string[] {
    const out: string[] = [];
    const errors = err?.error?.errors;

    if (Array.isArray(errors)) {
      errors.forEach((e: any) => out.push(String(e)));
    } else if (errors && typeof errors === 'object') {
      Object.keys(errors).forEach((key) => {
        const val = (errors as any)[key];
        if (Array.isArray(val)) {
          val.forEach((m: any) => out.push(String(m)));
        } else if (val != null) {
          out.push(String(val));
        }
      });
    }

    if (out.length === 0) {
      const message = err?.error?.message || err?.message;
      if (message) {
        out.push(String(message));
      }
    }

    return out.map((m) => m.trim()).filter(Boolean);
  }
}
